import React from 'react';
import { useTranslation } from 'react-i18next';

import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { TimePicker } from '@/components/ui/inputs/TimePicker';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useAdjustMarkForm } from '@/hooks/attendance/report/useAdjustMarkForm';
import { markingEventService } from '@/api/domains/catalog/eventType/eventType.service';
import { formatRut } from '@/utils';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';


const COMMENT_MAX_LENGTH = 500;
const COMMENT_TEXTAREA_ROWS = 3;

export interface AdjustMarkPanelProps {
  record: MarkReport | null;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export const AdjustMarkPanel: React.FC<AdjustMarkPanelProps> = ({
  record,
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation();

  const { options: eventOptions } = useCatalogLoader(
    (signal) => markingEventService.getMarkingEvents(signal),
    (event) => ({ value: String(event.id), label: event.description }),
  );

  const {
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    errors,
    isSubmitted,
    newTime,
    newEventId,
    comment,
    handleTimeChange,
    handleTimePickerOpenChange,
    handleEventChange,
    handleCommentChange,
  } = useAdjustMarkForm({ record, onSubmit, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="adjust-mark-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:marksReport.adjust.fields.rut')}
          value={record ? formatRut(record.rut) : ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.adjust.fields.employee')}
          value={record?.fullName ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.adjust.fields.date')}
          value={record?.markDate ?? record?.workdayDate ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.adjust.fields.time')}
          value={record?.markTime ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.adjust.fields.currentEvent')}
          value={record?.eventName ?? ''}
          disabled
        />

        <TimePicker
          label={translate('attendance:marksReport.adjust.fields.newTime')}
          value={newTime}
          onChange={handleTimeChange}
          onOpenChange={handleTimePickerOpenChange}
          disabled={effectiveReadOnly || isSubmitting}
          placeholder={translate('attendance:marksReport.adjust.placeholders.newTime')}
          color={errors.newTime && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.newTime?.message && isSubmitted
              ? translate(errors.newTime.message)
              : undefined
          }
        />

        <Selector
          label={translate('attendance:marksReport.adjust.fields.newEvent')}
          placeholder={translate('attendance:marksReport.adjust.placeholders.newEvent')}
          options={eventOptions}
          value={newEventId}
          onChange={(value) => handleEventChange(Array.isArray(value) ? value[0] ?? '' : value)}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.newEventId && isSubmitted ? 'error' : 'default'}
        />

        <TextArea
          label={translate('attendance:marksReport.adjust.fields.comment')}
          placeholder={translate('attendance:marksReport.adjust.placeholders.comment')}
          value={comment}
          onChange={handleCommentChange}
          disabled={effectiveReadOnly || isSubmitting}
          maxLength={COMMENT_MAX_LENGTH}
          required
          rows={COMMENT_TEXTAREA_ROWS}
          color={errors.comment && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.comment?.message && isSubmitted
              ? translate(errors.comment.message)
              : undefined
          }
          showCharacterCount
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <PermissionGuard action="edit">
          <SidePanelFormLayout.Footer>
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('attendance:marksReport.adjust.actions.save')}
            </PrimaryButton>
          </SidePanelFormLayout.Footer>
        </PermissionGuard>
      )}
    </SidePanelFormLayout>
  );
};

export default AdjustMarkPanel;
